import { router } from "@/app/router.ts";
import { useHasAccess } from "@/shared/access-control/composables/use-has-access";
import { useDocQueryParams } from "@/shared/composables/doc-query-params.ts";
import { TextObject } from "@/shared/i18n/translation-types";
import { useAbilityStore } from "@/shared/store/ability.store";
import { createNavigationEntries, NavigationEntry } from "@/shell/helpers/app-navigation";
import { storeToRefs } from "pinia";
import { computed, ComputedRef, Ref, ref } from "vue";
import { useContextNavigation } from "@/shared/ui-extensions/context-navigation.ts";
import { GuiElementView } from "@/shared/ui-extensions/context.types.ts";
import { useAbilityRoute } from "@/shared/environment/ability-route.ts";

export interface NavItem {
    visible: boolean;
    selected: boolean;
    index: number;
    icon: string;
    title: TextObject;
    path?: string;
    disabled?: boolean;
    alias?: string | undefined;
    externalLink?: string | undefined;
    target?: "_blank" | "_self";
    tooltip?: string;
}

export function useNavItems() {
    const abilityStore = useAbilityStore();
    const { abilities } = storeToRefs(abilityStore);
    const { queryParams } = useDocQueryParams();
    const { contextNavItems } = useContextNavigation({ view: GuiElementView.navigation_bar });

    const { getCurrentContextParams, getPathToChildWithoutParents } = useAbilityRoute();

    const accessGrantedForItem = (item: NavigationEntry): Ref<boolean> => {
        if (item.access) {
            const { hasAccess } = useHasAccess({
                action: item.access.accessAction,
                resource: item.access.accessResource,
                featureID: item.access.accessFeature,
                ignoreConditions: true,
            });
            return hasAccess;
        }
        return ref(true);
    };

    const abilityOfItemIsActive = (item: NavigationEntry): Ref<boolean> => {
        return computed(() => {
            return !!abilities?.value?.find((ability) => ability.alias === item.abilityAlias);
        });
    };

    const navigationEntries = createNavigationEntries();

    // im computed der navElements kann kein useQuery aufgerufen werden, da sonst der Komponentenbezug verloren geht.
    // Um auch die Sichtbarkeit durch die Berechtigungen zu bestimmen, werden die Ergebnisse der Rechte-Prüfung zu einer Route
    // als Workaround in einer Map zwischengespeichert
    // Für die ausgeblendeten Abilities gilt das gleiche, deshalb werden die erst hier ausgefiltert
    const itemIsVisibleMap = new Map<number, { hasAccess: Ref<boolean>; abilityIsActive: Ref<boolean> }>();
    navigationEntries.forEach((item) => {
        const hasAccess = accessGrantedForItem(item);
        const abilityIsActive = abilityOfItemIsActive(item);
        itemIsVisibleMap.set(item.index, {
            hasAccess: hasAccess,
            abilityIsActive: abilityIsActive,
        });
    });

    const navElements: ComputedRef<NavItem[]> = computed(() => {
        return navigationEntries.map((item) => {
            if (item.path && !/^\//.test(item.path)) {
                item.path = `/${item.path}`;
            }

            if (item.queryParams && Object.keys(queryParams.value).length > 0) {
                const searchParams = new URLSearchParams(queryParams.value);
                item.path = `${item.pathWithoutParam}?${searchParams.toString()}`;
            }

            // Trailing empty params must be omitted
            if (item.withContextParams && item.alias && item.routeParams) {
                const context = getCurrentContextParams(router.currentRoute.value);
                const namedParams = Object.fromEntries(
                    item.routeParams.map((p) => p.replace("?", "")).map((name) => [name, context[name] ?? ""])
                );
                const target = getPathToChildWithoutParents(item.alias, namedParams);
                const targetName = typeof target !== "string" && "name" in target ? target.name : undefined;

                // The ability routes are registered asynchronously and only where access is granted,
                // so a missing route is expected — the entry then keeps its default path.
                if (targetName && router.hasRoute(targetName)) {
                    try {
                        item.path = router.resolve(target).path;
                    } catch (e: unknown) {
                        // The context does not fit the route, e.g. a required param is missing
                        console.warn(
                            `Could not resolve path for navigation entry "${item.alias}": ${e instanceof Error ? e.message : String(e)}`
                        );
                    }
                }
            }

            function getSelected() {
                if (item.alias && item.pathWithoutParam) {
                    // Sub-routes are not supported
                    // Highlighting only applies to root-level routes
                    return router.currentRoute.value?.path?.startsWith(item.pathWithoutParam);
                } else {
                    return false;
                }
            }

            function getVisible() {
                const visible = item.isVisible ? item.isVisible() : true;
                const result = itemIsVisibleMap.get(item.index);
                if (result) {
                    return visible && result.hasAccess.value && result.abilityIsActive.value;
                } else {
                    return visible;
                }
            }

            return {
                ...item,
                visible: getVisible(),
                selected: getSelected(),
            };
        });
    });

    const allNavElements: ComputedRef<NavItem[]> = computed(() => {
        if (!contextNavItems.value) {
            return navElements.value;
        }

        const navElementsCopy = [...navElements.value];
        if (navElementsCopy.length === 0) return navElementsCopy;
        const lastElement = navElementsCopy[navElementsCopy.length - 1];

        let lastElementIndex = lastElement.index;
        const newElements = contextNavItems.value.map((element) => {
            return { ...element, index: ++lastElementIndex };
        });

        //remove last element
        navElementsCopy.pop();
        //add new elements
        navElementsCopy.push(...newElements);
        //add last element
        lastElement.index = ++lastElementIndex;
        navElementsCopy.push(lastElement);

        return navElementsCopy;
    });

    return { navElements: allNavElements };
}
